import { useSearchParams } from 'next/navigation'
import pluralize from 'pluralize'
import { stringifyUrl } from 'query-string'
import { Fragment, ReactElement, cloneElement, useMemo } from 'react'
import styled from 'styled-components'

import { Animate } from '@syconium/little-miss-figgy/dist/components/Animate'
import { Skeleton } from '@syconium/little-miss-figgy/dist/components/Skeleton'
import { TypeStyle } from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { fromMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'

import { FloatingFavoritesButton } from '../../../app/_components/buttons/FavoritesButton.client'
import { NostoPlacementId } from '../../../app/_components/chrome/scripts/NostoScript'
import { NextLink } from '../../../app/_components/navigation/NextLink'
import {
  PageSection,
  PageSectionContent,
  sectionSideGutterFromMdAndExpanded,
  sectionSideGutterUntilMd,
} from '../../../app/_components/pages/PageSection.client'
import { PricingRange } from '../../../app/_components/pricing/PricingRange.client'
import {
  ProductTileColorCount,
  ProductTileColorName,
  ProductTileColors,
  ProductTileColorsSkeleton,
  ProductTileDetails,
  ProductTileImage,
  ProductTileImageSkeleton,
  ProductTilePrice,
  ProductTilePriceSkeleton,
  ProductTileTitle,
  ProductTileTitleSkeleton,
} from '../../../app/_components/tiles/ProductTile.client'
import {
  LIFTED_BUTTONS_DEFAULT_PIXELS,
  Tile,
  TilesLayout,
} from '../../../app/_components/tiles/Tiles.client'
import { useTrackProductInteraction } from '../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { trackEvent } from '../../../lib/analytics'
import { useNostoRecos } from '../../../lib/hooks/useNostoRecos'

const StyledTitle = styled(TypeStyle.HeadlineSmall)`
  margin-bottom: 16px;

  ${fromMd} {
    margin-bottom: 24px;
  }
`

export interface NostoProductRecommendationsSectionProps {
  analyticsContext?: {
    category?: string
  }
  fadeIn?: boolean
  nostoPlacementId: NostoPlacementId
  /**
   * Optionally override the Nosto-provided recommendation title.
   * Pass a string literal as a simple replacement, or pass a function that is
   * given the Nosto-provided recommendation title and returns a transformed
   * title.
   */
  title?: string | ((nostoTitle: string | undefined) => string | undefined)
  wrapper?: ReactElement
}

export const NostoProductRecommendationsSection = ({
  analyticsContext,
  fadeIn,
  nostoPlacementId,
  title: titleOverride,
  wrapper,
}: NostoProductRecommendationsSectionProps) => {
  const {
    magnolia: {
      productCarouselColorCount: colorCountText,
      general: { scrollBack: scrollBackLabel, scrollForward: scrollForwardLabel },
    },
  } = useTranslation()
  const { trackProductInteraction } = useTrackProductInteraction()

  const {
    placementTitle: nostoTitle,
    variants: variantRecs,
    resultId: nostoProductsId,
    status,
  } = useNostoRecos({
    nostoPlacementId,
    productCountLimit: 8,
  })

  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined
  const variants = useMemo(() => {
    return (variantRecs ?? []).map(variantRec => {
      return {
        ...variantRec,
        urlPath: stringifyUrl({
          url: variantRec.urlPath,
          query: { nosto: nostoProductsId, plain: plainQueryParamValue },
        }),
      }
    })
  }, [nostoProductsId, plainQueryParamValue, variantRecs])

  const title = useMemo<string | undefined>(() => {
    return typeof titleOverride === 'function'
      ? titleOverride(nostoTitle)
      : titleOverride ?? nostoTitle
  }, [nostoTitle, titleOverride])

  const tilesLayoutChoices = useMemo(() => {
    return {
      sm: 'slider',
      md: 'slider',
    } as const
  }, [])

  const tilesPerViewportWidth = useMemo(() => {
    return {
      sm: 2,
      md: 4,
    }
  }, [])

  const tilesGutterChoices = useMemo(() => {
    return {
      sm: sectionSideGutterUntilMd,
      md: sectionSideGutterFromMdAndExpanded,
    } as const
  }, [])

  const skeletonTiles = useMemo(() => [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [])

  const OptionalFadeIn = fadeIn ? Animate.FadeIn : Fragment

  if (status === 'rejected' || (status === 'resolved' && variants.length === 0)) return null

  return cloneElement(wrapper ?? <></>, {
    children: (
      <PageSection>
        <PageSectionContent gutterSize='expanded'>
          <StyledTitle>
            {status === 'pending' ? <Skeleton animated height='42px' maxWidth='225px' /> : title}
          </StyledTitle>
        </PageSectionContent>
        <PageSectionContent gutterSize='none'>
          <OptionalFadeIn>
            <TilesLayout
              scrollBackLabel={scrollBackLabel}
              scrollForwardLabel={scrollForwardLabel}
              liftButtonsPixels={LIFTED_BUTTONS_DEFAULT_PIXELS}
              layout={tilesLayoutChoices}
              visibleTiles={tilesPerViewportWidth}
              gutters={tilesGutterChoices}
            >
              {status === 'pending'
                ? skeletonTiles.map((_, index) => {
                    return (
                      <Tile key={index}>
                        <ProductTileImageSkeleton aspectRatio={'slider'} />
                        <ProductTileDetails untilMdLeftPadding={false}>
                          <ProductTileTitleSkeleton />
                          <ProductTileColorsSkeleton />
                          <ProductTilePriceSkeleton />
                        </ProductTileDetails>
                      </Tile>
                    )
                  })
                : variants.map((variant, index) => {
                    const imageUrl = variant.group.images?.[0]?.url
                    if (!imageUrl) return null

                    return (
                      <Tile
                        key={`${variant.id}${index}`}
                        {...trackEvent({
                          category: analyticsContext?.category ?? 'product-carousel-tile',
                          action: 'click tile',
                          label: `tile-${index + 1}`,
                          value: variant.group.title,
                          correspondingAsset: imageUrl,
                        })}
                      >
                        <NextLink
                          href={variant.urlPath}
                          onClick={() => {
                            trackProductInteraction({
                              handle: variant.group.product.handle,
                              color: variant.group.color?.id ?? '',
                              id: variant.group.id,
                              shopifyId: variant.shopifyProductId,
                            })
                          }}
                        >
                          <ProductTileImage
                            alt={variant.group.title}
                            loading='lazy'
                            src={imageUrl}
                            aspectRatio={'slider'}
                            widths={{
                              unit: 'vw',
                              sm: 100 / tilesPerViewportWidth.sm,
                              md: 100 / tilesPerViewportWidth.md,
                            }}
                          />
                          <ProductTileDetails untilMdLeftPadding={false}>
                            <ProductTileTitle>{variant.group.title}</ProductTileTitle>
                            <ProductTileColors>
                              {variant.group.color?.id !== 'No Color' && (
                                <ProductTileColorName>
                                  {variant.group.color?.displayValue}
                                </ProductTileColorName>
                              )}
                              <ProductTileColorCount>
                                {variant.group.product.options.colorsTotalCount}{' '}
                                {pluralize(
                                  colorCountText,
                                  variant.group.product.options.colorsTotalCount
                                )}
                              </ProductTileColorCount>
                            </ProductTileColors>
                            {variant.pricing ? (
                              <ProductTilePrice>
                                <PricingRange
                                  {...variant.pricing}
                                  upToPercentageOff={null}
                                  percentageOff={null}
                                />
                              </ProductTilePrice>
                            ) : null}
                          </ProductTileDetails>
                        </NextLink>
                        {variant.group.color?.id &&
                          variant.fit?.id &&
                          variant.group.product.__typename === 'ProductCatalogStandardProduct' && (
                            <FloatingFavoritesButton
                              productGroupHandle={variant.group.product.handle}
                              colorKey={variant.group.color.id}
                              fitKey={variant.fit.id}
                              sizeKey={null}
                              productGroupImageSrc={imageUrl}
                            />
                          )}
                      </Tile>
                    )
                  })}
            </TilesLayout>
          </OptionalFadeIn>
        </PageSectionContent>
      </PageSection>
    ),
  })
}
